import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {isNullOrUndefined} from 'util';
import {LastQuoteCacheService} from '../../last-quote-cache.service';
import {MessageBusService} from '../../message-bus.service';
import {QuoteDto} from '../../shell-communication/dtos/quote-dto.class';
import {TradingInstrumentsService} from '../../trading-instruments/trading-instruments-service.interface';
import {isVoid, roundToStep} from '../../utils';
import {AtmStrikeChangedEvent} from "./atm-strike-changed-event";
import {AtmStrategy} from "./atm-strategy";
import {AtmWatcher} from "./atm-watcher";


@Injectable({
    providedIn: 'root'
})
export class AtmStrikeService {
    constructor(
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _tiService: TradingInstrumentsService,
    ) {

    }

    private _watchersByKey: { [key: string]: AtmWatcher } = {};
    private _watchersByTicker: { [ticker: string]: AtmWatcher[] } = {};
    private _subs: Subscription[] = [];

    //
    init(): Promise<void> {
        this.dispose();

        const start = Date.now();

        this._subs.push(
            this._messageBus.of<QuoteDto[]>('QuoteDto')
                .subscribe(msg => this.onQuote(msg.payload))
        );

        this._subs.push(
            this._messageBus.of<any>('AuthTokenExpiredUIMessage')
                .subscribe(() => this.dispose())
        );

        return this._lastQuoteCache.getLastQuoteWithAwait('SPX')
            .then(q => {
                return this._lastQuoteCache.getLastQuoteWithAwait('SPY');
            }).then(() => {
                return this._lastQuoteCache.getLastQuoteWithAwait(this._tiService.currentEsContract);
            }).then(() => {
                // Pre-subscribe ATM watchers for key symbols so they're ready when OPG restores state
                this.watch('SPX');
                this.watch('SPY');
                this.watch(this._tiService.currentEsContract);

                const end = Date.now();

                this._messageBus.publish({
                    topic: 'ServiceInitialized',
                    payload: {
                        time: end - start,
                        name: 'AtmStrikes'
                    }
                });

            });
    }

    //
    watch(underlying: string, strategy: AtmStrategy = 'Midpoint') {

        const key = this.makeKey(underlying, strategy);
        let watcher = this._watchersByKey[key];

        if (isNullOrUndefined(watcher)) {

            watcher = new AtmWatcher(underlying, strategy);

            watcher.atmChanged$.subscribe((data: AtmStrikeChangedEvent) => {
                const topic = data.strategy === 'Sticky'
                    ? 'AtmStrikeChangedEvent:Sticky'
                    : 'AtmStrikeChangedEvent';
                this._messageBus.publishAsync({
                    topic: topic,
                    payload: data
                });
            });

            this._watchersByKey[key] = watcher;

            if (isNullOrUndefined(this._watchersByTicker[underlying])) {
                this._watchersByTicker[underlying] = [];
            }
            this._watchersByTicker[underlying].push(watcher);

            this._lastQuoteCache.subscribeIfNotYet(underlying);

            const lq = this._lastQuoteCache.getLastQuote(underlying);

            if (lq) {
                watcher.onQuote(lq);
            }

            return;
        }

        watcher.broadcast();

    }

    //
    calculateCurrentAtm(underlying: string, lastPx: number): number {
        const increment = underlying === 'SPX' ? 5 : 1;
        return roundToStep(lastPx, increment);
    }

    //
    getCurrentAtm(underlying: string, strategy: AtmStrategy = 'Midpoint'): number {

        const key = this.makeKey(underlying, strategy);
        const watcher = this._watchersByKey[key];

        if (isVoid(watcher)) {
            return undefined;
        }

        let atm = watcher.atm;

        return atm;

    }

    //
    dispose() {
        this._subs.forEach(s => s.unsubscribe());
        this._subs = [];
        this._watchersByKey = {};
        this._watchersByTicker = {};
    }

    //
    private onQuote(quotes: QuoteDto[]) {
        quotes.forEach(q => this.deliverQuoteToWatch(q));
    }

    //
    private deliverQuoteToWatch(quote: QuoteDto) {
        const watchers = this._watchersByTicker[quote.ticker];

        if (isNullOrUndefined(watchers)) {

            return;
        }

        watchers.forEach(w => w.onQuote(quote));

        // if (this._timestampsService.isPreMarketHours()) {
        //     if (quote.ticker === this._tiService.currentEsContract) {
        //         const lastPx = this._lastQuoteCache.getLastQuote('SPX')?.lastPx;
        //         const delta = this._spxAfterHoursDiffService.getMarketDifference();
        //         const adjusted = lastPx + delta;
        //         const spxQuote: QuoteDto = { lastPx: adjusted, ticker: 'SPX', mid: adjusted } as any;
        //         this.deliverQuoteToWatch(spxQuote);
        //     }
        // }
    }

    private fakeEmitter(underlying: string, atm: number) {

        const data: AtmStrikeChangedEvent = {
            currentAtm: atm,
            previousAtm: undefined,
            underlying: underlying
        };

        this._messageBus.publishAsync({
            topic: 'AtmStrikeChangedEvent',
            payload: data
        });
    }

    //
    private makeKey(underlying: string, strategy: AtmStrategy): string {
        return underlying + '::' + strategy;
    }
}
